import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ChevronUp, ChevronDown } from "lucide-react";

const SiteSettingsManager = () => {
  const [signupsEnabled, setSignupsEnabled] = useState(true);
  const [showFrameworks, setShowFrameworks] = useState(true);
  const [showAITools, setShowAITools] = useState(true);
  const [showPrompts, setShowPrompts] = useState(true);
  const [resourcesOrder, setResourcesOrder] = useState<string[]>(["frameworks", "ai_tools", "prompts"]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await supabase
        .from('site_settings')
        .select('setting_key, setting_value, is_public')
        .in('setting_key', ['signups_enabled', 'show_frameworks', 'show_ai_tools', 'show_prompts', 'resources_order']);
      
      if (data) {
        data.forEach((setting) => {
          if (setting.setting_key === 'resources_order') {
            try {
              setResourcesOrder(JSON.parse(setting.setting_value));
            } catch (e) {
              console.error('Error parsing resources_order:', e);
            }
          } else {
            const value = setting.setting_value === 'true';
            switch (setting.setting_key) {
              case 'signups_enabled':
                setSignupsEnabled(value);
                break;
              case 'show_frameworks':
                setShowFrameworks(value);
                break;
              case 'show_ai_tools':
                setShowAITools(value);
                break;
              case 'show_prompts':
                setShowPrompts(value);
                break;
            }
          }
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const updateResourcesOrder = async (newOrder: string[]) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('site_settings')
        .update({ setting_value: JSON.stringify(newOrder) })
        .eq('setting_key', 'resources_order');

      if (error) throw error;

      setResourcesOrder(newOrder);
      toast({
        title: "Settings Updated",
        description: "Resources order updated successfully.",
      });
    } catch (error) {
      console.error('Error updating settings:', error);
      toast({
        title: "Error",
        description: "Failed to update settings.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const moveResourceUp = (index: number) => {
    if (index > 0) {
      const newOrder = [...resourcesOrder];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      updateResourcesOrder(newOrder);
    }
  };

  const moveResourceDown = (index: number) => {
    if (index < resourcesOrder.length - 1) {
      const newOrder = [...resourcesOrder];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      updateResourcesOrder(newOrder);
    }
  };

  const getResourceLabel = (key: string) => {
    const labels: Record<string, string> = {
      frameworks: 'Frameworks',
      ai_tools: 'AI Tools',
      prompts: 'AI Prompts'
    };
    return labels[key] || key;
  };

  const updateSetting = async (settingKey: string, enabled: boolean, settingName: string) => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('site_settings')
        .update({ 
          setting_value: enabled.toString(),
          is_public: true // Settings toggled here are public-facing
        })
        .eq('setting_key', settingKey);

      if (error) throw error;

      switch (settingKey) {
        case 'signups_enabled':
          setSignupsEnabled(enabled);
          break;
        case 'show_frameworks':
          setShowFrameworks(enabled);
          break;
        case 'show_ai_tools':
          setShowAITools(enabled);
          break;
        case 'show_prompts':
          setShowPrompts(enabled);
          break;
      }

      toast({
        title: "Settings Updated",
        description: `${settingName} ${enabled ? 'enabled' : 'disabled'}.`,
      });
    } catch (error) {
      console.error('Error updating settings:', error);
      toast({
        title: "Error",
        description: "Failed to update settings.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Site Settings</CardTitle>
        <CardDescription>
          Manage global site configuration and access controls.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="signups-toggle" className="text-base">
              Allow New Signups
            </Label>
            <p className="text-sm text-muted-foreground">
              Control whether new users can create accounts on your site.
            </p>
          </div>
          <Switch
            id="signups-toggle"
            checked={signupsEnabled}
            onCheckedChange={(enabled) => updateSetting('signups_enabled', enabled, 'New user signups')}
            disabled={loading}
          />
        </div>

        <div className="pt-6 border-t space-y-6">
          <h3 className="text-lg font-semibold">Resource Visibility</h3>
          <p className="text-sm text-muted-foreground">
            Control which resource categories appear on the /resources page.
          </p>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="frameworks-toggle" className="text-base">
                Show Frameworks
              </Label>
              <p className="text-sm text-muted-foreground">
                Display the frameworks section on the resources page.
              </p>
            </div>
            <Switch
              id="frameworks-toggle"
              checked={showFrameworks}
              onCheckedChange={(enabled) => updateSetting('show_frameworks', enabled, 'Frameworks section')}
              disabled={loading}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="ai-tools-toggle" className="text-base">
                Show AI Tools
              </Label>
              <p className="text-sm text-muted-foreground">
                Display the AI Tools section on the resources page.
              </p>
            </div>
            <Switch
              id="ai-tools-toggle"
              checked={showAITools}
              onCheckedChange={(enabled) => updateSetting('show_ai_tools', enabled, 'AI Tools section')}
              disabled={loading}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="prompts-toggle" className="text-base">
                Show AI Prompts
              </Label>
              <p className="text-sm text-muted-foreground">
                Display the AI prompts section on the resources page.
              </p>
            </div>
            <Switch
              id="prompts-toggle"
              checked={showPrompts}
              onCheckedChange={(enabled) => updateSetting('show_prompts', enabled, 'AI Prompts section')}
              disabled={loading}
            />
          </div>
        </div>

        <div className="pt-6 border-t space-y-6">
          <h3 className="text-lg font-semibold">Resource Order</h3>
          <p className="text-sm text-muted-foreground">
            Drag or use arrows to reorder sections on the /resources page.
          </p>

          <div className="space-y-2">
            {resourcesOrder.map((resourceKey, index) => (
              <div key={resourceKey} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <span className="font-semibold text-sm w-8">{index + 1}.</span>
                <span className="flex-1 font-medium">{getResourceLabel(resourceKey)}</span>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => moveResourceUp(index)}
                    disabled={index === 0 || loading}
                  >
                    <ChevronUp className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => moveResourceDown(index)}
                    disabled={index === resourcesOrder.length - 1 || loading}
                  >
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SiteSettingsManager;